/**
 * Firebase Profile Service
 * Handles user profile operations in Firestore
 */

import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  limit 
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Initialize user profile in Firestore
 * 
 * @param {string} userId - User ID
 * @param {Object} userData - User data
 * @returns {Promise<void>}
 */
export const initializeUserProfile = async (userId, userData) => {
  const profileData = {
    photos: [],
    basicInfo: {
      name: userData.name,
      username: userData.username,
      email: userData.email,
      phone: '',
      gender: '',
      interestedIn: [],
      ageRange: '',
      starSign: ''
    },
    academic: {
      university: userData.university,
      degree: '',
      yearOfStudy: '',
      residence: '',
      studentNumber: ''
    },
    location: {
      hometown: '',
      currentLocation: '',
      residenceType: ''
    },
    bio: {
      shortBio: '',
      longBio: '',
      personalities: [],
      interests: [],
      relationshipGoal: ''
    },
    languages: ['English'],
    preferences: {
      showInMeetups: true,
      showOtherUniversities: true,
      darkMode: false,
      activityStatus: 'Available',
      emailNotifications: true,
      pushNotifications: true
    },
    privacy: {
      profileVisibility: 'Public',
      showOnlineStatus: true,
      showLastSeen: true,
      showReadReceipts: true
    },
    social: {
      instagram: '',
      linkedin: '',
      github: ''
    },
    createdAt: new Date(),
    lastUpdated: new Date()
  };

  try {
    const profileRef = doc(db, 'profiles', userId);
    await setDoc(profileRef, profileData);
    console.log('User profile created successfully');
  } catch (error) {
    console.error('Error initializing user profile:', error);
    throw error;
  }
};

/**
 * Check if username exists
 * 
 * @param {string} username - Username to check
 * @returns {Promise<boolean>} True if username exists, false otherwise
 */
export const checkUsernameExists = async (username) => {
  if (!username || username.length < 3) return false;
  
  try {
    const usersRef = collection(db, "profiles");
    const q = query(
      usersRef,
      where("basicInfo.username", "==", username.toLowerCase()),
      limit(1)
    );

    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking username:', error);
    // Return false on error to prevent blocking registration
    return false;
  }
};

/**
 * Get user profile
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User profile data or null if not found
 */
export const getUserProfile = async (userId) => {
  try {
    const profileRef = doc(db, 'profiles', userId);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      return { id: profileSnap.id, ...profileSnap.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

/**
* Update user profile
* 
* @param {string} userId - User ID
* @param {Object} profileData - Profile data to update
* @returns {Promise<void>}
*/
export const updateUserProfile = async (userId, profileData) => {
 try {
   const profileRef = doc(db, 'profiles', userId);
   await updateDoc(profileRef, {
     ...profileData,
     lastUpdated: new Date()
   });
   console.log('User profile updated successfully');
 } catch (error) {
   console.error('Error updating user profile:', error);
   throw error;
 }
};

/**
* Get profile by username
* 
* @param {string} username - Username to search for
* @returns {Promise<Object|null>} User profile data or null if not found
*/
export const getProfileByUsername = async (username) => {
 if (!username) return null;
 
 try {
   const usersRef = collection(db, "profiles");
   const q = query(
     usersRef,
     where("basicInfo.username", "==", username.toLowerCase()),
     limit(1)
   );

   const querySnapshot = await getDocs(q);
   
   if (!querySnapshot.empty) {
     const doc = querySnapshot.docs[0];
     return { id: doc.id, ...doc.data() };
   }
   
   return null;
 } catch (error) {
   console.error('Error getting profile by username:', error);
   throw error;
 }
};

/**
* Check if user profile exists
* 
* @param {string} userId - User ID
* @returns {Promise<boolean>} True if profile exists, false otherwise
*/
export const checkProfileExists = async (userId) => {
 try {
   const profileRef = doc(db, 'profiles', userId);
   const profileSnap = await getDoc(profileRef);
   return profileSnap.exists();
 } catch (error) {
   console.error('Error checking if profile exists:', error);
   return false;
 }
};